import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePermissions } from '@Core/Hooks/usePermissions'
import { identityAPI } from '@User/Data/DataSource/API/IdentityAPI'
import { DBUser } from '@User/Domain/Model/User'
import { roles } from '@User/roles'
import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'react-toastify'

interface Props {
    user: DBUser
    onChanged: () => void | Promise<void>
}

/**
 * Authorized password exception (FR-016c): after the transition only these
 * users may sign in with e-mail and password, at /login/alternativo.
 */
export const PasswordExceptionSection = ({ user, onChanged }: Props) => {
    const { hasPermission } = usePermissions()
    const canEdit = hasPermission(roles.user.permissions.edit.key)
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const allowed = user.passwordLoginAllowed === true
    const exception = user.passwordLoginException

    const update = async (nextAllowed: boolean) => {
        if (nextAllowed && !reason.trim()) {
            toast.error('Informe o motivo da exceção')
            return
        }
        setBusy(true)
        try {
            await identityAPI.setPasswordException(
                user.uid,
                nextAllowed,
                nextAllowed ? reason.trim() : undefined
            )
            toast.success(
                nextAllowed ? 'Login com senha permitido' : 'Exceção de senha revogada'
            )
            setReason('')
            await onChanged()
        } catch (error) {
            console.error(error)
            toast.error('Erro ao atualizar a exceção de login com senha')
        }
        setBusy(false)
    }

    return (
        <section className="flex flex-col gap-3" data-testid="password-exception-section">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold">Login com senha</h2>
                {allowed ? (
                    <Badge variant="secondary">Exceção (senha autorizada)</Badge>
                ) : (
                    <Badge variant="outline">Somente Microsoft</Badge>
                )}
            </div>
            {allowed && exception && (
                <p className="text-sm text-muted-foreground">
                    {exception.reason}
                    {exception.at ? ` · desde ${format(new Date(exception.at), 'dd/MM/yyyy')}` : ''}
                </p>
            )}
            {canEdit && allowed && (
                <div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => update(false)}
                    >
                        Revogar
                    </Button>
                </div>
            )}
            {canEdit && !allowed && (
                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        aria-label="Motivo da exceção"
                        className="max-w-sm"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Motivo (ex.: fornecedor sem conta Microsoft)"
                    />
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy || !reason.trim()}
                        onClick={() => update(true)}
                    >
                        Permitir login com senha
                    </Button>
                </div>
            )}
        </section>
    )
}
